import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { BrioModal, BrioButton } from '../../components/BrioComponents';
import { SOURCE_FILES_BUCKET, isPdfPath, sourceFileSignedUrl } from '../utils/sourceFileStorage';

// ============================================================================
// Source preview — the place in its source file a library rate was read from.
// ============================================================================
// A PDF opens at the page (the browser's own viewer, so it reads exactly as
// the file does). A workbook is read in the browser with ExcelJS and the
// rows around the rate's row are drawn as the sheet lays them out, with that
// row picked out, so the figure can be checked against its neighbours.
//
// The stored path is looked up when the modal opens rather than taken from
// the rate row, so a file uploaded on Settings → Source files a moment ago
// previews without reloading the rates.
// ============================================================================

const ROWS_AROUND = 14;     // rows shown above and below the rate's row
const MAX_COLS = 30;        // wide comparison sheets: enough for every bidder
const MIN_COL_PX = 44;      // an empty column still reads as a column
const MAX_COL_PX = 320;     // a long description wraps rather than running on
const ROW_HEAD_PX = 44;

// Workbooks are parsed once per session; rates from one file share it.
const workbookCache = new Map();

async function loadWorkbook(path) {
  if (workbookCache.has(path)) return workbookCache.get(path);
  const promise = (async () => {
    const { data, error } = await supabase.storage.from(SOURCE_FILES_BUCKET).download(path);
    if (error) throw error;
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await data.arrayBuffer());
    return wb;
  })();
  workbookCache.set(path, promise);
  promise.catch(() => workbookCache.delete(path));
  return promise;
}

const colLetter = (n) => {
  let s = '';
  for (let x = n; x > 0; x = Math.floor((x - 1) / 26)) s = String.fromCharCode(65 + ((x - 1) % 26)) + s;
  return s;
};

// What the cell shows: a formula's result, rich text joined, numbers as Excel
// would print them without its number format.
function cellText(cell) {
  const v = cell.value;
  if (v == null) return '';
  if (typeof v === 'object') {
    if (v.richText) return v.richText.map((t) => t.text).join('');
    if ('result' in v) return v.result == null ? '' : (typeof v.result === 'number' ? fmtNum(v.result) : String(v.result));
    if (v.text) return String(v.text);
    if (v instanceof Date) return v.toLocaleDateString();
    if (v.error) return String(v.error);
  }
  if (typeof v === 'number') return fmtNum(v);
  return String(v);
}
const fmtNum = (n) => (Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));

function findSheet(wb, name) {
  if (!name) return null;
  return wb.getWorksheet(name)
    || wb.worksheets.find((ws) => ws.name.trim().toLowerCase() === name.trim().toLowerCase())
    || null;
}

function SheetExcerpt({ path, sheetName, row }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    setState({ loading: true });
    loadWorkbook(path)
      .then((wb) => {
        if (!live) return;
        const ws = findSheet(wb, sheetName);
        if (!ws) { setState({ error: `Sheet "${sheetName}" is not in the stored file.` }); return; }
        const from = Math.max(1, row - ROWS_AROUND);
        const to = Math.min(ws.rowCount || row, row + ROWS_AROUND);
        let lastCol = 1;
        const rows = [];
        for (let r = from; r <= to; r += 1) {
          const cells = [];
          ws.getRow(r).eachCell({ includeEmpty: false }, (c, col) => {
            if (col <= MAX_COLS) { cells[col] = cellText(c); if (col > lastCol) lastCol = col; }
          });
          rows.push({ r, cells });
        }
        setState({ rows, lastCol, sheet: ws.name });
      })
      .catch((err) => {
        console.error('[SourcePreviewModal] workbook failed', err);
        if (live) setState({ error: 'Could not read the stored workbook.' });
      });
    return () => { live = false; };
  }, [path, sheetName, row]);

  if (state.loading) return <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>Reading the workbook…</div>;
  if (state.error) return <div style={{ padding: '1rem', color: '#a94442' }}>{state.error}</div>;

  const cols = Array.from({ length: state.lastCol }, (_, i) => i + 1);
  // Each column as wide as its longest line (capped), so text wraps between
  // words instead of collapsing to a few letters per line. A wide sheet then
  // scrolls sideways inside the box, with the row numbers held on the left.
  const colWidth = (c) => {
    const longest = state.rows.reduce((m, { cells }) => {
      const lines = String(cells[c] || '').split('\n');
      return Math.max(m, ...lines.map((l) => l.length));
    }, 0);
    return Math.min(MAX_COL_PX, Math.max(MIN_COL_PX, longest * 7 + 14));
  };
  const widths = cols.map(colWidth);
  const tableWidth = ROW_HEAD_PX + widths.reduce((t, w) => t + w, 0);
  const head = { position: 'sticky', top: 0, background: 'var(--brio-surface-muted, #f2f2ef)', color: 'var(--brio-text-muted)', fontWeight: 600, zIndex: 1 };
  const rowHead = { position: 'sticky', left: 0, color: 'var(--brio-text-muted)', textAlign: 'right', paddingRight: 6, background: 'var(--brio-surface-muted, #f7f7f5)', zIndex: 1 };
  return (
    <div style={{ overflow: 'auto', maxHeight: '68vh', border: '1px solid var(--brio-border, #ddd)' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed', width: tableWidth }}>
        <colgroup>
          <col style={{ width: ROW_HEAD_PX }} />
          {widths.map((w, i) => <col key={cols[i]} style={{ width: w }} />)}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...head, ...rowHead, zIndex: 2 }} />
            {cols.map((c) => <th key={c} style={{ ...head, padding: '2px 6px', borderLeft: '1px solid var(--brio-border-subtle, #e4e4e0)' }}>{colLetter(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {state.rows.map(({ r, cells }) => {
            const hit = r === row;
            return (
              <tr key={r} style={hit ? { background: 'var(--brio-gold-light, rgba(159,144,42,0.18))', outline: '2px solid var(--brio-gold, #9F902A)' } : undefined}>
                <td style={{ ...rowHead, fontWeight: hit ? 700 : 400 }}>{r}</td>
                {cols.map((c) => (
                  <td key={c} style={{
                    padding: '3px 6px', verticalAlign: 'top', whiteSpace: 'pre-wrap', overflowWrap: 'break-word',
                    borderTop: '1px solid var(--brio-border-subtle, #eee)', borderLeft: '1px solid var(--brio-border-subtle, #eee)',
                    fontWeight: hit ? 600 : 400,
                  }}>{cells[c] || ''}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SourcePreviewModal({ rate, onClose }) {
  const [doc, setDoc] = useState({ loading: true });
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const { data, error } = await supabase.schema('boq').from('rate_source_documents')
        .select('filename, storage_path').eq('id', rate.sourceDocumentId).maybeSingle();
      if (!live) return;
      if (error) { setDoc({ error: 'Could not look up the source file.' }); return; }
      setDoc({ filename: data?.filename || rate.source, path: data?.storage_path || null });
    })();
    return () => { live = false; };
  }, [rate.sourceDocumentId, rate.source]);

  const pdf = isPdfPath(doc.path) || (!doc.path && /\.pdf$/i.test(doc.filename || ''));

  useEffect(() => {
    let live = true;
    if (doc.path && pdf) {
      sourceFileSignedUrl(doc.path).then((u) => { if (live) setPdfUrl(u); });
    }
    return () => { live = false; };
  }, [doc.path, pdf]);

  const where = rate.sourcePage
    ? `page ${rate.sourcePage}`
    : rate.sourceSheet ? `${rate.sourceSheet.trim()} · row ${rate.sourceRow}` : 'location not recorded';
  const figure = rate.rateQuoted ?? rate.rateBilled;
  const title = useMemo(() => `${rate.fullCode} · ${doc.filename || rate.source || 'Source file'}`, [rate.fullCode, rate.source, doc.filename]);

  const download = async () => {
    const u = await sourceFileSignedUrl(doc.path, { download: true });
    if (u) window.open(u, '_blank', 'noopener');
  };

  const footer = (
    <>
      {doc.path && (
        <BrioButton variant="secondary" onClick={download} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
          <Download size={14} /> Download file
        </BrioButton>
      )}
      <BrioButton variant="primary" onClick={onClose}>Close</BrioButton>
    </>
  );

  return (
    <BrioModal title={title} onClose={onClose} width="xl" footer={footer}>
      <div style={{ fontSize: '0.82rem', marginBottom: '0.6rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <span><strong>{where}</strong></span>
        <span style={{ color: 'var(--brio-text-muted)' }}>{rate.shortDescription}</span>
        {figure != null && (
          <span>Rate <strong>{fmtNum(Number(figure))}</strong>{rate.unit ? ` / ${rate.unit}` : ''}{rate.bidBy ? ` · ${rate.bidBy}` : ''}</span>
        )}
      </div>

      {doc.loading ? (
        <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>Looking up the file…</div>
      ) : doc.error ? (
        <div style={{ padding: '1rem', color: '#a94442' }}>{doc.error}</div>
      ) : !doc.path ? (
        <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>
          {doc.filename} has not been uploaded yet. Upload it on <strong>Settings → Source files</strong> (File column)
          and every rate read from it will open here.
        </div>
      ) : pdf ? (
        pdfUrl
          ? <iframe title={doc.filename} src={`${pdfUrl}#page=${rate.sourcePage || 1}`} style={{ width: '100%', height: '70vh', border: '1px solid var(--brio-border, #ddd)' }} />
          : <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>Opening the PDF…</div>
      ) : rate.sourceSheet && rate.sourceRow ? (
        <SheetExcerpt path={doc.path} sheetName={rate.sourceSheet} row={rate.sourceRow} />
      ) : (
        <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>This rate has no recorded sheet and row. Download the file to look it up.</div>
      )}
    </BrioModal>
  );
}

export default SourcePreviewModal;
